import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FileText, Clock, Award, TrendingUp, Loader2, Filter,
  Search, Grid, List, CheckCircle, Target, BarChart3,
  BookOpen, Zap, Star, AlertCircle, Package, Bookmark as BookmarkIcon,
  Play, RotateCcw, Trash2, Layers
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_STYLE = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700'
};

const getDifficultyColor = (d) =>
  DIFFICULTY_STYLE[(d || 'medium').toLowerCase()] || 'bg-gray-100 text-gray-700';

// localStorage key used by TakeTest for in-progress save
const progressKey = (studentId, testId) => `vidya365:test-progress:${studentId}:${testId}`;

const StudentTestsEnhanced = () => {
  const navigate = useNavigate();
  const studentUser = (() => {
    try { return JSON.parse(localStorage.getItem('studentUser') || '{}'); } catch (e) { return {}; }
  })();
  const studentId = studentUser.id || studentUser.email || 'anon';

  const [tests, setTests] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({ category: 'all', difficulty: 'all', search: '' });

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchAll(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async (token) => {
    setLoading(true);
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      const [testRes, attemptsRes, bookmarksRes] = await Promise.all([
        fetch(`${API_URL}/api/tests`),
        fetch(`${API_URL}/api/tests/my-attempts`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bookmarks`, { headers: authHeaders })
      ]);
      const testData = testRes.ok ? await testRes.json() : [];
      const attemptData = attemptsRes.ok ? await attemptsRes.json() : [];
      const bookmarkData = bookmarksRes.ok ? await bookmarksRes.json() : [];
      setTests(testData);
      setAttempts(attemptData);
      setBookmarks(bookmarkData);
    } catch (e) {
      console.error('fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  // -------------- derived values --------------
  const attemptsByTest = useMemo(() => {
    const map = {};
    for (const a of attempts) {
      if (!map[a.test_id] || new Date(a.submitted_at) > new Date(map[a.test_id].submitted_at)) {
        map[a.test_id] = a;
      }
    }
    return map;
  }, [attempts]);

  // Resume detection: tests with saved progress in localStorage
  const resumableTestIds = useMemo(() => {
    const ids = new Set();
    try {
      const prefix = `vidya365:test-progress:${studentId}:`;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          ids.add(k.substring(prefix.length));
        }
      }
    } catch (e) { /* noop */ }
    return ids;
  }, [studentId, tests]);

  const stats = useMemo(() => {
    const completed = attempts.length;
    const avg = completed
      ? (attempts.reduce((s, a) => s + (a.percentage || 0), 0) / completed).toFixed(1)
      : 0;
    const best = completed ? Math.max(...attempts.map(a => a.percentage || 0)).toFixed(1) : 0;
    return { completed, avg, best, totalTests: tests.length };
  }, [attempts, tests]);

  const performanceData = useMemo(() => {
    // Last 10 attempts, oldest first, for the line chart
    const sorted = [...attempts]
      .filter(a => a.submitted_at)
      .sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
      .slice(-10);
    return sorted.map((a, i) => ({
      test: `T${i + 1}`,
      score: Math.round(a.percentage || 0),
      date: new Date(a.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
    }));
  }, [attempts]);

  const focusAreas = useMemo(() => {
    // Accuracy by test subject (proxy for weak areas)
    const map = {};
    for (const a of attempts) {
      const test = tests.find(t => t.id === a.test_id);
      const subject = test?.subject || 'General';
      if (!map[subject]) map[subject] = { total: 0, correct: 0, wrong: 0, attempts: 0 };
      map[subject].attempts += 1;
      map[subject].correct += a.correct_answers || 0;
      map[subject].wrong += a.wrong_answers || 0;
      map[subject].total += (a.correct_answers || 0) + (a.wrong_answers || 0) + (a.unanswered || 0);
    }
    return Object.entries(map).map(([subject, v]) => {
      const accuracy = v.total ? Math.round((v.correct / v.total) * 100) : 0;
      return { subject, accuracy, needsPractice: accuracy < 60 };
    }).sort((a, b) => a.accuracy - b.accuracy);
  }, [attempts, tests]);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(tests.map(t => t.course_category).filter(Boolean)))],
    [tests]
  );

  const filteredTests = useMemo(() => {
    return tests.filter(test => {
      if (filters.category !== 'all' && test.course_category !== filters.category) return false;
      if (filters.difficulty !== 'all' && (test.difficulty || 'medium').toLowerCase() !== filters.difficulty) return false;
      if (filters.search && !test.title.toLowerCase().includes(filters.search.toLowerCase())) return false;

      if (activeTab === 'attempted' && !attemptsByTest[test.id]) return false;
      if (activeTab === 'unattempted' && attemptsByTest[test.id]) return false;
      if (activeTab === 'resume' && !resumableTestIds.has(test.id)) return false;
      return true;
    });
  }, [tests, filters, activeTab, attemptsByTest, resumableTestIds]);

  // Group tests by subject for "By Subject" view
  const testsBySubject = useMemo(() => {
    const map = {};
    for (const t of filteredTests) {
      const key = t.subject || 'General';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return map;
  }, [filteredTests]);

  // Test Packs grouped by course_category (real data)
  const testPacks = useMemo(() => {
    const map = {};
    for (const t of tests) {
      const key = t.course_category || 'General';
      if (!map[key]) map[key] = { id: key, name: `${key} Test Pack`, category: key, tests: [], validity: '6 months' };
      map[key].tests.push(t);
    }
    return Object.values(map).map(p => {
      const completed = p.tests.filter(t => attemptsByTest[t.id]).length;
      const scores = p.tests.map(t => attemptsByTest[t.id]?.percentage).filter(v => v != null);
      const avg = scores.length ? (scores.reduce((s, x) => s + x, 0) / scores.length).toFixed(0) : 0;
      return {
        ...p,
        totalTests: p.tests.length,
        completedTests: completed,
        averageScore: avg,
        description: `${p.tests.length} curated mock tests for ${p.category}`
      };
    });
  }, [tests, attemptsByTest]);

  const handleRemoveBookmark = async (id) => {
    const token = localStorage.getItem('studentToken');
    try {
      const res = await fetch(`${API_URL}/api/bookmarks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setBookmarks(prev => prev.filter(b => b.id !== id));
    } catch (e) { console.error(e); }
  };

  const handleDiscardResume = (testId) => {
    try {
      localStorage.removeItem(progressKey(studentId, testId));
      // force re-derive
      setTests(t => [...t]);
    } catch (e) { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 py-12" data-testid="tests-hero">
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Test Series Dashboard</h1>
          <p className="text-lg text-red-100">Practice, Analyze and Excel in Your Exams</p>
        </div>
      </section>

      {/* Quick stats */}
      <section className="py-6 bg-white border-b" data-testid="tests-stats">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={<FileText size={28} className="text-blue-600" />} value={stats.totalTests} label="Total Tests" testid="stat-total" />
            <StatCard icon={<CheckCircle size={28} className="text-green-600" />} value={stats.completed} label="Completed" testid="stat-completed" />
            <StatCard icon={<TrendingUp size={28} className="text-orange-600" />} value={`${stats.avg}%`} label="Avg Score" testid="stat-avg" />
            <StatCard icon={<Award size={28} className="text-purple-600" />} value={`${stats.best}%`} label="Best Score" testid="stat-best" />
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-6">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center text-base">
                    <Filter className="mr-2" size={18} /> Filters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <select
                      data-testid="filter-category"
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Difficulty</label>
                    <select
                      data-testid="filter-difficulty"
                      value={filters.difficulty}
                      onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="all">All Levels</option>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <Button
                    data-testid="filter-clear"
                    variant="outline"
                    className="w-full"
                    onClick={() => setFilters({ category: 'all', difficulty: 'all', search: '' })}
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>

              {/* Performance Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-base">
                    <BarChart3 className="mr-2" size={18} /> Performance Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {performanceData.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">Take a test to see your trend</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={performanceData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" fontSize={10} />
                        <YAxis fontSize={10} domain={[0, 100]} />
                        <Tooltip />
                        <Line type="monotone" dataKey="score" stroke="#dc2626" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Focus Areas */}
              {focusAreas.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-base">
                      <AlertCircle className="mr-2" size={18} /> Focus Areas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {focusAreas.filter(f => f.needsPractice).slice(0, 4).map((f, i) => (
                      <div key={i} className="bg-red-50 p-3 rounded-lg border border-red-200">
                        <p className="text-sm font-medium">{f.subject}</p>
                        <p className="text-xs text-red-600">Accuracy: {f.accuracy}%</p>
                      </div>
                    ))}
                    {focusAreas.filter(f => f.needsPractice).length === 0 && (
                      <p className="text-sm text-green-700">Great job! No weak areas detected.</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </aside>

            {/* Main content */}
            <div className="lg:col-span-3">
              {/* Tabs */}
              <div className="mb-6 bg-white rounded-lg shadow-sm p-1 flex flex-wrap gap-1" data-testid="tests-tabs">
                {[
                  { key: 'all', label: 'All Tests' },
                  { key: 'packs', label: 'Test Packs' },
                  { key: 'subjects', label: 'By Subject' },
                  { key: 'resume', label: `Resume${resumableTestIds.size ? ` (${resumableTestIds.size})` : ''}` },
                  { key: 'attempted', label: 'Attempted' },
                  { key: 'unattempted', label: 'Unattempted' },
                  { key: 'bookmarks', label: `Bookmarks${bookmarks.length ? ` (${bookmarks.length})` : ''}` }
                ].map(tab => (
                  <button
                    key={tab.key}
                    data-testid={`tab-${tab.key}`}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 min-w-[120px] py-2 px-3 rounded-md font-medium text-sm transition-colors ${
                      activeTab === tab.key ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search + view toggle (hide for packs & bookmarks) */}
              {activeTab !== 'packs' && activeTab !== 'bookmarks' && (
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      data-testid="filter-search"
                      type="text"
                      placeholder="Search tests..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="flex border border-gray-300 rounded-md">
                    <button data-testid="view-grid" onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-red-600 text-white' : 'text-gray-600'}`}>
                      <Grid size={20} />
                    </button>
                    <button data-testid="view-list" onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-red-600 text-white' : 'text-gray-600'}`}>
                      <List size={20} />
                    </button>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
                </div>
              ) : (
                <>
                  {/* Bookmarks tab */}
                  {activeTab === 'bookmarks' && (
                    <BookmarksView bookmarks={bookmarks} onRemove={handleRemoveBookmark} />
                  )}

                  {/* Packs tab */}
                  {activeTab === 'packs' && (
                    <PacksView packs={testPacks} onOpen={(catId) => { setFilters({ ...filters, category: catId }); setActiveTab('all'); }} />
                  )}

                  {/* By Subject */}
                  {activeTab === 'subjects' && (
                    <SubjectsView groupings={testsBySubject} attemptsByTest={attemptsByTest} resumableTestIds={resumableTestIds} onDiscardResume={handleDiscardResume} />
                  )}

                  {/* All / attempted / unattempted / resume */}
                  {(activeTab === 'all' || activeTab === 'attempted' || activeTab === 'unattempted' || activeTab === 'resume') && (
                    filteredTests.length === 0 ? (
                      <Card>
                        <CardContent className="text-center py-12">
                          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                          <p className="text-gray-600">No tests found for this view.</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 gap-6' : 'space-y-4'}>
                        {filteredTests.map(test => (
                          <TestCard
                            key={test.id}
                            test={test}
                            variant={viewMode}
                            attempt={attemptsByTest[test.id]}
                            isResumable={resumableTestIds.has(test.id)}
                            onDiscardResume={handleDiscardResume}
                          />
                        ))}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// -------- Reusable subcomponents --------
const StatCard = ({ icon, value, label, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4 text-center">
      <div className="flex justify-center mb-2">{icon}</div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-gray-600">{label}</p>
    </CardContent>
  </Card>
);

const TestCard = ({ test, variant, attempt, isResumable, onDiscardResume }) => {
  const difficultyBadge = (
    <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(test.difficulty)}`}>
      {test.difficulty || 'Medium'}
    </span>
  );

  const actionSection = (
    <div className="flex gap-2">
      {isResumable ? (
        <>
          <Link className="flex-1" to={`/student/test/${test.id}`}>
            <Button data-testid={`resume-test-${test.id}`} className="w-full bg-amber-600 hover:bg-amber-700">
              <Play className="mr-2" size={16} /> Resume
            </Button>
          </Link>
          <Button
            data-testid={`discard-resume-${test.id}`}
            variant="outline"
            onClick={() => onDiscardResume(test.id)}
            title="Discard saved progress"
          >
            <RotateCcw size={16} />
          </Button>
        </>
      ) : attempt ? (
        <>
          <Link className="flex-1" to={`/student/test/${test.id}/result`} state={{ result: { ...attempt, total_questions: test.question_count, test_name: test.title } }}>
            <Button data-testid={`view-result-${test.id}`} variant="outline" className="w-full">
              View Result ({Math.round(attempt.percentage)}%)
            </Button>
          </Link>
          <Link to={`/student/test/${test.id}`}>
            <Button data-testid={`retake-test-${test.id}`} className="bg-red-600 hover:bg-red-700">
              Retake
            </Button>
          </Link>
        </>
      ) : (
        <Link className="flex-1" to={`/student/test/${test.id}`}>
          <Button data-testid={`start-test-${test.id}`} className="w-full bg-red-600 hover:bg-red-700">
            <BookOpen className="mr-2" size={16} /> Start Test
          </Button>
        </Link>
      )}
    </div>
  );

  if (variant === 'list') {
    return (
      <Card className="hover:shadow-lg transition-shadow" data-testid={`test-card-${test.id}`}>
        <CardContent className="p-6 flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-medium">
                {test.course_category}
              </span>
              {difficultyBadge}
              {isResumable && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium">In Progress</span>}
              {attempt && !isResumable && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium">Attempted</span>}
            </div>
            <h3 className="text-lg font-bold mb-1">{test.title}</h3>
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{test.description}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center"><FileText className="mr-1" size={14} /> {test.question_count} Qs</span>
              <span className="flex items-center"><Clock className="mr-1" size={14} /> {test.duration_minutes} mins</span>
              <span className="flex items-center"><Award className="mr-1" size={14} /> {test.total_marks} marks</span>
            </div>
          </div>
          <div className="w-64">{actionSection}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-xl transition-shadow flex flex-col" data-testid={`test-card-${test.id}`}>
      <CardHeader className="bg-gradient-to-r from-red-50 to-red-100">
        <div className="flex justify-between items-start mb-2">
          <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-medium">
            {test.course_category}
          </span>
          {difficultyBadge}
        </div>
        <CardTitle className="text-xl">{test.title}</CardTitle>
        {isResumable && <span className="mt-1 inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium w-fit">⏱ In Progress</span>}
        {attempt && !isResumable && (
          <span className="mt-1 inline-block px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium w-fit">
            Attempted • {Math.round(attempt.percentage)}%
          </span>
        )}
      </CardHeader>
      <CardContent className="p-6 space-y-4 flex-1 flex flex-col">
        <p className="text-sm text-gray-600 line-clamp-2">{test.description}</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center"><FileText className="mr-2 text-gray-400" size={16} /> {test.question_count} Qs</div>
          <div className="flex items-center"><Clock className="mr-2 text-gray-400" size={16} /> {test.duration_minutes} mins</div>
          <div className="flex items-center"><Award className="mr-2 text-gray-400" size={16} /> {test.total_marks} marks</div>
          <div className="flex items-center"><Target className="mr-2 text-gray-400" size={16} /> {test.passing_marks} to pass</div>
        </div>
        <div className="mt-auto pt-2">{actionSection}</div>
      </CardContent>
    </Card>
  );
};

const PacksView = ({ packs, onOpen }) => (
  <div className="space-y-6" data-testid="packs-view">
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
      <h2 className="text-2xl font-bold mb-2 flex items-center">
        <Package className="mr-2" size={24} /> Test Series Packs
      </h2>
      <p className="text-gray-600">Comprehensive test bundles for complete preparation</p>
    </div>
    {packs.length === 0 ? (
      <Card><CardContent className="py-12 text-center text-gray-600">No packs available yet.</CardContent></Card>
    ) : (
      <div className="grid md:grid-cols-2 gap-6">
        {packs.map(pack => (
          <Card key={pack.id} className="border-2 hover:border-purple-500 transition-all" data-testid={`pack-${pack.id}`}>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex justify-between items-start">
                <div>
                  <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs font-medium mb-2 inline-block">
                    {pack.category}
                  </span>
                  <CardTitle className="text-xl">{pack.name}</CardTitle>
                </div>
                <Star className="text-yellow-500 fill-yellow-500" size={24} />
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-gray-600">{pack.description}</p>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium">{pack.completedTests}/{pack.totalTests} tests</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full"
                    style={{ width: `${pack.totalTests ? (pack.completedTests / pack.totalTests) * 100 : 0}%` }}
                  />
                </div>
                {pack.averageScore > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Average Score</span>
                    <span className="font-medium text-green-600">{pack.averageScore}%</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Validity</span>
                  <span className="font-medium">{pack.validity}</span>
                </div>
              </div>
              <Button
                data-testid={`open-pack-${pack.id}`}
                onClick={() => onOpen(pack.category)}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Zap className="mr-2" size={16} />
                {pack.completedTests > 0 ? 'Continue Pack' : 'Start Pack'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    )}
  </div>
);

const SubjectsView = ({ groupings, attemptsByTest, resumableTestIds, onDiscardResume }) => {
  const entries = Object.entries(groupings);
  if (entries.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center text-gray-600">No tests match your filters.</CardContent></Card>
    );
  }
  return (
    <div className="space-y-8" data-testid="subjects-view">
      {entries.map(([subject, list]) => (
        <div key={subject}>
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <Layers className="mr-2 text-red-600" size={20} />
            {subject}
            <span className="ml-2 text-sm text-gray-500">({list.length})</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {list.map(t => (
              <TestCard
                key={t.id}
                test={t}
                variant="grid"
                attempt={attemptsByTest[t.id]}
                isResumable={resumableTestIds.has(t.id)}
                onDiscardResume={onDiscardResume}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const BookmarksView = ({ bookmarks, onRemove }) => {
  const [expanded, setExpanded] = useState({});
  if (bookmarks.length === 0) {
    return (
      <Card data-testid="bookmarks-empty">
        <CardContent className="text-center py-12">
          <BookmarkIcon className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">No bookmarks yet. Bookmark questions from test reviews to revise them later.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-4" data-testid="bookmarks-view">
      {bookmarks.map(b => {
        const isOpen = !!expanded[b.id];
        return (
          <Card key={b.id} className="border-l-4 border-l-amber-500" data-testid={`bookmark-${b.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">{b.test_title}</span>
                    {b.subject && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{b.subject}</span>}
                    <span className="text-gray-500">Q{b.question_index + 1}</span>
                  </div>
                  <p className="font-medium mb-3">{b.question_text}</p>

                  <button
                    data-testid={`toggle-answer-${b.id}`}
                    onClick={() => setExpanded(p => ({ ...p, [b.id]: !isOpen }))}
                    className="text-sm text-red-600 hover:underline mb-2"
                  >
                    {isOpen ? 'Hide answer' : 'Show answer'}
                  </button>

                  {isOpen && (
                    <div className="space-y-2 mt-2">
                      {b.options.map((opt, idx) => (
                        <div
                          key={idx}
                          className={`px-3 py-2 rounded border text-sm ${
                            idx === b.correct_answer
                              ? 'bg-green-50 border-green-300 text-green-800 font-medium'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          {String.fromCharCode(65 + idx)}. {opt}
                          {idx === b.correct_answer && <CheckCircle className="inline ml-2" size={14} />}
                        </div>
                      ))}
                      {b.explanation && (
                        <div className="mt-2 bg-blue-50 border border-blue-200 p-3 rounded text-sm">
                          <strong>Explanation:</strong> {b.explanation}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  data-testid={`remove-bookmark-${b.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => onRemove(b.id)}
                  title="Remove bookmark"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default StudentTestsEnhanced;
